import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Faq } from '../components/Faq'
import { useReveal } from '../components/marketing/motion'
import faqs from '../data/faqs.json'
import { LANES, type Lane } from '../data/lanes'
import { MIN_TRADE_LANES, tradeSlug } from '../data/trades'
import { usePageMeta } from '../lib/usePageMeta'

// Preferred display order for trade groups; unknown trades fall to the end alphabetically.
// Mirrors TRADE_ORDER in prerender.lanes.mjs so the SPA hub matches the prerendered one.
const TRADE_ORDER = [
  'Asia–Europe',
  'Transpacific',
  'Transatlantic',
  'Intra-Asia',
  'Middle East',
  'Oceania',
  'Africa',
  'Latin America',
  'Intra-North America',
]

function orderedTrades(trades: string[]): string[] {
  return [...trades].sort((a, b) => {
    const ia = TRADE_ORDER.indexOf(a)
    const ib = TRADE_ORDER.indexOf(b)
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
    return a.localeCompare(b)
  })
}

export function LanesHubPage() {
  usePageMeta(
    'Ocean shipping lanes & sailing schedules · SchedulesMCP',
    `Sailing schedules, carrier line-ups and transit times for ${LANES.length} ocean lanes: Asia–Europe, Transpacific, Intra-Asia and more. Compare carriers on any lane before you book.`
  )

  const groups = useMemo(() => {
    const byTrade = new Map<string, Lane[]>()
    for (const l of LANES) {
      const list = byTrade.get(l.trade) ?? []
      list.push(l)
      byTrade.set(l.trade, list)
    }
    for (const list of byTrade.values()) list.sort((a, b) => b.sailings - a.sailings)
    return orderedTrades([...byTrade.keys()]).map((trade) => ({
      trade,
      // biome-ignore lint/style/noNonNullAssertion: keys come from byTrade itself
      lanes: byTrade.get(trade)!,
    }))
  }, [])

  useReveal('main')

  return (
    <main className="max-w-4xl mx-auto px-6 pt-12 pb-16">
      <nav className="text-sm text-[var(--text-muted)] mb-10">
        <Link to="/" className="no-underline hover:underline">
          SchedulesMCP
        </Link>{' '}
        › Lanes
      </nav>

      {/* Hero, book §07 anatomy: teal eyebrow → ultralight title → one-line hook → live lead */}
      <header className="text-center mb-14">
        <p
          className="m-0 font-mono text-xs uppercase text-accent"
          style={{ letterSpacing: '0.14em' }}
        >
          Lane coverage
        </p>
        <h1
          className="mx-auto mt-10 mb-0 font-display text-[clamp(28px,4.6vw,48px)] leading-[1.08] text-[var(--text-primary)]"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Ocean shipping lanes &amp; schedules
        </h1>
        <p className="mx-auto mt-10 mb-0 text-base leading-[1.5] text-[var(--text-secondary)]">
          Forward sailings, carrier line-ups and transit times for every lane we cover, grouped by
          trade.
        </p>
        <p className="mx-auto mt-6 mb-0 max-w-[62ch] text-sm leading-relaxed text-[var(--text-muted)] text-justify [hyphens:auto]">
          {LANES.length} ocean lanes with forward sailings, carrier line-ups, median transit times
          and upcoming departures, each normalised into one shape so you can compare carriers before
          you book. Pick a lane, or jump into the live compare tool.
        </p>
      </header>

      {groups.map(({ trade, lanes }) => (
        <section key={trade} className="mb-9">
          <h2 className="font-display font-bold text-2xl mb-1">
            {lanes.length >= MIN_TRADE_LANES ? (
              <Link
                to={`/lanes/trade/${tradeSlug(trade)}`}
                className="no-underline hover:underline text-[var(--text-primary)]"
              >
                {trade === 'Oceania' ? 'Pacific Islands' : trade}
              </Link>
            ) : trade === 'Oceania' ? (
              'Pacific Islands'
            ) : (
              trade
            )}{' '}
            <span className="text-[var(--text-muted)] text-sm font-normal">
              · {lanes.length} lanes
            </span>
          </h2>
          <ul className="list-none p-0 m-0">
            {lanes.map((l) => (
              <li key={l.slug} className="py-2 border-b border-border">
                <Link
                  to={`/lanes/${l.slug}`}
                  className="text-signal font-semibold no-underline hover:underline"
                >
                  {l.o_name} → {l.d_name}
                </Link>
                <span className="text-[var(--text-muted)] text-sm">
                  {' '}
                  · {l.sailings} sailings · {l.carriers} carriers
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to="/compare"
          className="bg-signal text-on-accent font-bold px-6 py-3 rounded-lg no-underline hover:opacity-90"
        >
          Compare a lane live
        </Link>
        <Link
          to="/carriers"
          className="border border-border px-6 py-3 rounded-lg no-underline hover:border-signal"
        >
          Browse carriers
        </Link>
      </div>

      <Faq id="lanes" items={faqs.lanes} heading="Ocean shipping lanes: FAQ" />
    </main>
  )
}

export default LanesHubPage
